import { useEffect, useRef, useState } from 'react';
import { track } from '@/lib/analytics';

const faqs = [
  {
    question: 'Wie funktioniert das virtuelle Staging?',
    answer: 'Du lädst ein Foto eines leeren Raums hoch, wählst den Raumtyp und Einrichtungsstil — unsere KI generiert dann ein realistisch eingerichtetes Bild. Das Ganze dauert nur wenige Sekunden.',
  },
  {
    question: 'Kann ich möblierte Räume auch leer räumen?',
    answer: 'Ja! Unsere "Leerräumen"-Funktion entfernt digital alle Möbel und Gegenstände aus einem Raum. Perfekt für Immobilien, die noch bewohnt sind und du den Käufern das Potenzial zeigen willst.',
  },
  {
    question: 'Welche Bildformate werden unterstützt?',
    answer: 'Wir unterstützen JPEG, PNG und HEIC (iPhone-Format). HEIC-Bilder werden automatisch konvertiert.',
  },
  {
    question: 'Wie viele Credits brauche ich pro Objekt?',
    answer: 'Pro generiertem Bild wird 1 Credit verbraucht – Nachbessern und Anpassungen sind kostenlos. Für ein typisches Objekt mit 8-12 Fotos brauchst du also 8-12 Credits. Dein Abo lädt die Credits jeden Monat neu auf (Starter 15, Pro 50, Agency 129), zusätzliche Credits kannst du jederzeit dazukaufen – die verfallen nie.',
  },
  {
    question: 'Was passiert, wenn meine Monats-Credits nicht reichen?',
    answer: 'Kein Problem: Du kaufst einfach zusätzliche Bild-Credits per Schieberegler dazu (1,50 € pro Credit). Zugekaufte Credits verfallen nie und werden erst genutzt, wenn deine Monats-Credits aufgebraucht sind. Grundrisse laufen separat: per Grundriss-Paket im Abo oder einzeln für 15 € (2D + 3D).',
  },
  {
    question: 'Kann ich mein eigenes Branding verwenden?',
    answer: 'Ja! Du kannst dein Logo, Watermark und individuelle Overlays hochladen und automatisch auf alle generierten Bilder anwenden lassen.',
  },
  {
    question: 'Werden meine Bilder gespeichert?',
    answer: 'Deine Bilder werden sicher in deinem Account gespeichert, sodass du jederzeit darauf zugreifen und sie erneut herunterladen kannst. Nur du hast Zugriff auf deine Projekte.',
  },
  {
    question: 'Wie realistisch sind die Ergebnisse wirklich?',
    answer: 'Die KI generiert fotorealistische Einrichtungen mit korrekten Proportionen, Lichtverhältnissen und Schatten. Viele Makler setzen unsere Bilder direkt für Exposés und Online-Portale ein — ohne dass Interessenten den Unterschied erkennen.',
  },
  {
    question: 'Darf ich KI-generierte Bilder überhaupt in Exposés verwenden?',
    answer: 'Ja, solange klar erkennbar ist, dass es sich um visualisierte Einrichtungsvorschläge handelt. Mit unserem integrierten Branding- und Watermark-Tool kannst du das transparent kennzeichnen und rechtssicher verwenden.',
  },
  {
    question: 'Funktioniert Estage auch für kommerzielle Immobilien?',
    answer: 'Ja — Estage funktioniert für Wohnungen, Häuser, Büros, Gewerbeflächen und Gastronomie. Die KI erkennt den Raumtyp automatisch und schlägt passende Stile vor.',
  },
  {
    question: 'Wie schnell ist der Support bei Fragen?',
    answer: 'Wir antworten in der Regel innerhalb weniger Stunden. Für dringende Anliegen findest du uns direkt unter info@estage.ai oder über den Kontakt-Button im Footer.',
  },
];

const ChevronIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/>
  </svg>
);

export const FAQSection: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number>(0);
  const circlesRef = useRef<HTMLImageElement>(null);

  const toggle = (i: number) => setOpenIndex(openIndex === i ? -1 : i);

  // Rotate the Circles decoration slightly based on scroll position
  useEffect(() => {
    const el = circlesRef.current;
    if (!el) return;
    let raf = 0;
    const update = () => {
      const rect = el.getBoundingClientRect();
      const viewportH = window.innerHeight || 1;
      // progress: 0 when section enters viewport bottom, 1 when it leaves at top
      const progress = 1 - (rect.top + rect.height * 0.5) / (viewportH + rect.height * 0.5);
      const clamped = Math.max(-0.5, Math.min(1.5, progress));
      el.style.transform = `rotate(${clamped * 60}deg)`;
    };
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => { update(); raf = 0; });
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <section className="lp-faq-section" id="faq">
      <img ref={circlesRef} src="/landing/Circles.svg" alt="" className="lp-faq-circles-bg" />
      <div className="lp-faq-inner">
        <div className="lp-faq-title">
          <h2>Du fragst<br />wir <span>antworten</span></h2>
        </div>
        <div className="lp-faq-list">
          {faqs.map((faq, i) => (
            <div
              key={i}
              className={`lp-faq-item${openIndex === i ? ' active' : ''}`}
              onClick={() => { toggle(i); if (openIndex !== i) track('FAQ Expanded', { question_index: i }); }}
            >
              <div className="lp-faq-question">
                <h3>{faq.question}</h3>
                <div className="lp-faq-icon"><ChevronIcon /></div>
              </div>
              <div className="lp-faq-answer">
                <p>{faq.answer}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
